import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  memo,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
  FormHelperText,
  Skeleton,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MultiStarIcon from "../../../assets/icons/multi_star.png";
import MultiStarThemeIcon from "../../../assets/icons/theme_star.png";
import VerifiedIcon from "../../../assets/icons/verified.png";

import Step1BasicInfoSkeleton from "../skeletons/Step1BasicInfoSkeleton";

import {
  Field,
  FieldType,
  Step1Values,
  ApplicantType,
  ProductOption,
} from "../types";
import { PRODUCTS } from "../utils/util";
import dropdownService from "../../../services/api/service/dropdownService";
import RightPanel from "../components/RightPanel";
import postsServices from "../../../services/api/service/postsService";
import { authStatusSelector } from "../../../services/redux/slices/authSlice";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentTrackingId } from "../../../services/redux/slices/newJourneyEligibilitycheckSlice";
import eligibilityCheckServices from "../../../services/api/service/eligibilityCheckService";
import { setBasicDetails } from "../../../services/redux/slices/newJourneyEligibilitycheckSlice";
import { getValidationSchema } from "../../../utils/validationSchemas";
import { JOURNEY_EMAIL_ERROR, JOURNEY_EMAIL_REGEX } from "../../../utils/emailValidation";
import {
  setIsEmailVerified,
  setIsMobileVerified,
  setSelectedProduct,
  setTrackingId,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import Sidebar from "../components/Sidebar";
import StickyFooter from "../components/StickyFooter";

interface Step1Props {
  fields: Field[];
  initialValues: Partial<Step1Values>;
  onNext: (values: Step1Values) => void;
  currentStep: number;
  progress: number;
  completedSteps: any
}

interface Step1FormValues {
  product: string;
  applicantType: ApplicantType;
  firstName: string;
  lastName: string;
  phone_number: string;
  email: string;
  pincode: string;
  terms_conditions: boolean;
  privacy_policy: boolean;
}

type Step1FieldName = keyof Step1FormValues;
type OtpModalType = "mobile" | "email" | null;

const buildValidationSchema = (fields: Field[]) => {
  const hasField = (name: string) =>
    fields.some((f) => f.name === name && !f.isRemoved);

  return Yup.object({
    product: Yup.string().notRequired(),

    applicantType: Yup.string().notRequired(), // handled separately via continueClicked flag

    firstName: hasField("firstName")
      ? Yup.string().trim().required("Please enter the student's first name.")
      : Yup.string().notRequired(),

    lastName: hasField("lastName")
      ? Yup.string().trim().required("Please enter the student's last name.")
      : Yup.string().notRequired(),

    phone_number: hasField("phone_number")
      ? Yup.string()
          .trim()
          .required("Please enter a valid 10-digit mobile number.")
          .matches(/^\d{10}$/, "Please enter a valid 10-digit mobile number.")
      : Yup.string().notRequired(),

    email: hasField("email")
      ? Yup.string()
          .trim()
          .required(
            "Please enter a valid email address (e.g., name@example.com).",
          )
          .email("Please enter a valid email address (e.g., name@example.com).")
          .matches(JOURNEY_EMAIL_REGEX, JOURNEY_EMAIL_ERROR)
      : Yup.string().notRequired(),

    pincode: hasField("pincode")
      ? Yup.string()
          .trim()
          .required("Please enter a valid 6-digit pincode.")
          .matches(/^\d{6}$/, "Please enter a valid 6-digit pincode.")
      : Yup.string().notRequired(),

    terms_conditions: hasField("terms_conditions")
      ? Yup.boolean().oneOf([true], "Consent is required to continue.")
      : Yup.boolean().notRequired(),

    privacy_policy: hasField("privacy_policy")
      ? Yup.boolean().oneOf([true], "Consent is required to continue.")
      : Yup.boolean().notRequired(),
  });
};

const FIELD_PRIORITY_ORDER: Step1FieldName[] = [
  "applicantType",
  "firstName",
  "lastName",
  "phone_number",
  "email",
  "pincode",
  "terms_conditions",
  "privacy_policy",
];

const isRenderable = (field: Field): boolean =>
  !field.isRemoved && field.type !== "boolean" && field.type !== "hidden";

type FieldGroup =
  | { kind: "singleton"; field: Field }
  | { kind: "pair"; left: Field; right: Field };

const groupFields = (fields: Field[]): FieldGroup[] => {
  const PAIRABLE: FieldType[] = ["text", "email", "phone"];
  const groups: FieldGroup[] = [];
  const sorted = [...fields]
    .filter(isRenderable)
    .sort((a, b) => a.fieldOrder - b.fieldOrder);

  let i = 0;
  while (i < sorted.length) {
    const current = sorted[i];
    const next = sorted[i + 1];

    if (
      PAIRABLE.includes(current.type) &&
      next !== undefined &&
      PAIRABLE.includes(next.type)
    ) {
      groups.push({ kind: "pair", left: current, right: next });
      i += 2;
    } else {
      groups.push({ kind: "singleton", field: current });
      i += 1;
    }
  }
  return groups.filter((grp: any) => grp?.field?.name !== "product");
};

const HtmlLabel: React.FC<{ html: string }> = ({ html }) => (
  <Typography
    component="span"
    sx={{
      fontSize: "0.78rem",
      color: "text.secondary",
      lineHeight: 1.6,
      "& a": { color: "#006763", textDecoration: "underline", fontWeight: 600 },
    }}
    dangerouslySetInnerHTML={{ __html: html }}
  />
);

interface OtpModalProps {
  open: boolean;
  type: OtpModalType;
  maskedDestination: string;
  otpValue: string;
  verifying: boolean;
  resendCooldown: number;
  onOtpChange: (v: string) => void;
  onVerify: () => Promise<void>;
  onResend: () => Promise<void>;
  onClose: () => void;
}

const OtpModal: React.FC<OtpModalProps> = ({
  open,
  type,
  maskedDestination,
  otpValue,
  verifying,
  resendCooldown,
  onOtpChange,
  onVerify,
  onResend,
  onClose,
}) => {
  const isPhone = type === "mobile";
  const title = isPhone
    ? "Verify your mobile number"
    : "Verify your email address";
  const icon = isPhone ? (
    <SmartphoneIcon
      sx={{ fontSize: { xs: 24, sm: 28 }, color: "primary.main" }}
    />
  ) : (
    <EmailOutlinedIcon
      sx={{ fontSize: { xs: 24, sm: 28 }, color: "primary.main" }}
    />
  );
  const sentTo = isPhone
    ? `Enter the 6-digit code sent to +91 ${maskedDestination}`
    : `Enter the 6-digit code sent to ${maskedDestination}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 2.5, sm: 3 },
          p: { xs: 0, sm: 0.5 },
          m: { xs: 2, sm: 4 },
          width: { xs: "calc(100% - 32px)", sm: "auto" },
          maxWidth: { xs: "calc(100% - 32px)", sm: 444 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 12 },
            right: { xs: 8, sm: 12 },
            color: "text.secondary",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 3, sm: 4 },
          px: { xs: 2.5, sm: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: { xs: 2, sm: 3 },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "16px", sm: "18px" },
              mb: 0.75,
              textAlign: "center",
              color: "#006763",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "12.5px", sm: "14px" },
              color: "#3D4948",
              textAlign: "center",
              lineHeight: 1.6,
              fontWeight: 600,
              px: { xs: 0.5, sm: 0 },
            }}
          >
            {sentTo}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            "& > *": { maxWidth: "100%" },
          }}
        >
          <OtpBoxInput value={otpValue} onChange={onOtpChange} />
        </Box>

        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={onVerify}
          disabled={otpValue.length < 6 || verifying}
          sx={{
            mt: { xs: 2.5, sm: 3 },
            py: { xs: 1.25, sm: 1.5 },
            fontWeight: 700,
            fontSize: { xs: "0.9rem", sm: "0.95rem" },
            minWidth: "auto",
            border: "1px solid #FFC703",
            backgroundColor: "#FFC703",
            color: "#000",
            "&:hover": {
              backgroundColor: "#FFC703",
              border: "1px solid #FFC703",
            },
          }}
        >
          {verifying ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Verify OTP"
          )}
        </Button>

        <Box
          sx={{
            mt: { xs: 2, sm: 2.5 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            flexWrap: "wrap",
          }}
        >
          {resendCooldown > 0 ? (
            <Typography
              sx={{
                fontSize: { xs: "0.78rem", sm: "0.82rem" },
                color: "text.secondary",
              }}
            >
              Resend OTP{" "}
              <Box
                component="span"
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                {`${String(Math.floor(resendCooldown / 60)).padStart(2, "0")}:${String(resendCooldown % 60).padStart(2, "0")}`}
              </Box>
            </Typography>
          ) : (
            <Button
              variant="text"
              size="small"
              onClick={onResend}
              sx={{
                fontSize: { xs: "0.78rem", sm: "0.82rem" },
                fontWeight: 600,
                color: "primary.main",
                p: 0,
                minWidth: "auto",
                textTransform: "none",
              }}
            >
              Resend OTP
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const OtpBoxInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => {
  const digits = value.split("").concat(Array(6).fill("")).slice(0, 6);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number,
  ): void => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = value.slice(
        0,
        idx > 0 && value.length <= idx ? idx - 1 : idx,
      );
      onChange(next.slice(0, 6));
      if (idx > 0) document.getElementById(`otp-box-${idx - 1}`)?.focus();
    } else if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const arr = value.split("").concat(Array(6).fill("")).slice(0, 6);
      arr[idx] = e.key;
      onChange(arr.filter(Boolean).join("").slice(0, 6));
      if (idx < 5) document.getElementById(`otp-box-${idx + 1}`)?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    onChange(pasted);
    document.getElementById(`otp-box-${Math.min(pasted.length, 5)}`)?.focus();
  };

  return (
    <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
      {digits.map((digit, idx) => (
        <Box
          key={idx}
          id={`otp-box-${idx}`}
          component="input"
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={() => {}}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
            handleKeyDown(e, idx)
          }
          onPaste={handlePaste}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          sx={{
            width: { xs: 40, sm: 56 },
            height: { xs: 40, sm: 56 },
            borderRadius: { xs: 0.8, sm: 2 },
            border: "2px solid",
            borderColor: digit ? "primary.main" : "grey.300",
            bgcolor: digit ? "rgba(27,107,90,0.04)" : "background.paper",
            textAlign: "center",
            fontSize: { xs: "0.85rem", sm: "1rem" },
            fontWeight: { xs: 600, sm: 700 },
            color: "text.primary",
            outline: "none",
            fontFamily: "Jost",
            cursor: "text",
            transition: "border-color 0.15s",
            "&:focus": {
              borderColor: "primary.main",
              boxShadow: "0 0 0 3px rgba(27,107,90,0.12)",
            },
          }}
        />
      ))}
    </Box>
  );
};

interface TextFieldRendererProps {
  field: Field;
  formik: ReturnType<typeof useFormik<Step1FormValues>>;
  fieldSize: "small" | "medium";
  otpSent: boolean;
  verified: boolean;
  otpValue: string;
  verifying: boolean;
  onSendOtp: () => Promise<void>;
  onVerify: () => Promise<void>;
  onOtpChange: (v: string) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  otpPendingError?: string;
}

const TextFieldRenderer: React.FC<TextFieldRendererProps> = ({
  field,
  formik,
  fieldSize,
  otpSent,
  verified,
  onSendOtp,
  onChange,
  otpPendingError,
}) => {
  const name = field.name as Step1FieldName;
  const value = formik.values[name];
  const touched = Boolean(formik.touched[name]);
  const yupError = formik.errors[name] as string | undefined;
  const hasYupError = touched && Boolean(yupError);
  const effectiveError = hasYupError ? yupError : otpPendingError;

  const isPhone = field.type === "phone";
  const isEmail = field.type === "email";
  const isPincode = field.name === "pincode";
  const isOtpField = isPhone || isEmail;

  const endAdornment = isOtpField ? (
    verified ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#EBF5F6",
          borderRadius: "12px",
          p: { xs: 1.5, sm: 2.5 },
        }}
      >
        <img src={VerifiedIcon} alt="Verified Icon" />
      </Box>
    ) : (
      <Button
        size="medium"
        onClick={onSendOtp}
        disabled={otpSent}
        variant={otpSent ? "text" : "outlined"}
        sx={{
          fontSize: "0.85rem",
          p: { xs: "12px 34px", sm: "20px 28px" },
          minWidth: "auto",
          border: "1px solid #FFC703",
          backgroundColor: "#FFC703",
          color: "#251A00",
          "&:hover": {
            backgroundColor: "#FFC703",
            border: "1px solid #FFC703",
          },
        }}
      >
        {otpSent ? "Sent" : "Verify"}
      </Button>
    )
  ) : null;

  return (
    <Box data-field-name={field.name} sx={{ width: "100%" }}>
      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#3D4948",
          mb: 0.75,
        }}
      >
        {field.label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "start", gap: "8px" }}>
        <TextField
          fullWidth
          size={fieldSize}
          name={field.name}
          type={isEmail ? "email" : "text"}
          inputMode={isPhone || isPincode ? "numeric" : undefined}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          onBlur={formik.handleBlur}
          error={Boolean(effectiveError)}
          helperText={effectiveError}
          required={field.required}
          placeholder={`Enter ${field.label}`}
          inputProps={{ maxLength: isPincode ? 6 : isPhone ? 10 : undefined }}
          // InputProps={{ endAdornment: endAdornment ?? undefined }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "#F0F2F5",
              padding: "4px 0px",
              color: "#000",
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": { border: "none" },
              "&.Mui-error fieldset": {
                border: "1.5px solid",
                borderColor: "error.main",
              },
            },
            "& .  -input::placeholder": { color: "#aaa", opacity: 1 },
          }}
        />
        {endAdornment && endAdornment}
      </Box>
    </Box>
  );
};

const ProductCardSkeleton: React.FC = () => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 2.5,
      minHeight: "200px",
      border: "2px solid #E7E8EA",
      bgcolor: "#f4f5f8",
    }}
  >
    <Skeleton
      variant="rectangular"
      width={48}
      height={48}
      sx={{ mb: 1.5, borderRadius: 1 }}
    />
    <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.75 }} />
    <Skeleton variant="text" width="100%" height={16} />
    <Skeleton variant="text" width="90%" height={16} />
    <Skeleton variant="text" width="60%" height={16} />
  </Paper>
);

const RESEND_COOLDOWN_SECONDS = 180;

const Step1BasicInfo: React.FC<Step1Props> = ({
  fields,
  initialValues,
  onNext,
  currentStep,
  progress,
  completedSteps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fieldSize: "small" | "medium" = isMobile ? "small" : "medium";
  const formRef = useRef<HTMLDivElement>(null);

  const [otpModal, setOtpModal] = useState<OtpModalType>(null);
  const [mobileOtp, setMobileOtp] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [verifyingMobile, setVerifyingMobile] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(
    initialValues?.isMobileVerified ?? false,
  );
  const [emailVerified, setEmailVerified] = useState(
    initialValues?.isEmailVerified ?? false,
  );
  const [mobileResendCooldown, setMobileResendCooldown] = useState(0);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [mobileOtpMeta, setMobileOtpMeta] = useState<{
    key: string;
    createdAt: string;
    mobileNumber: string;
  } | null>(null);
  const [emailOtpMeta, setEmailOtpMeta] = useState<{
    key: string;
    createdAt: string;
    email: string;
  } | null>(null);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  // Gates OTP-pending & applicantType error display
  const [continueClicked, setContinueClicked] = useState(false);

  const isAuthenticated = useSelector(authStatusSelector);
  const trackingId = useSelector(
    (state: any) => state?.eligibilityCheck?.trackingId ?? "",
  );
  const dispatch = useDispatch();

  const fetchProducts = useCallback(async (): Promise<void> => {
    setProductsLoading(true);
    try {
      const res = await dropdownService.getProducts();
      const mapped = (res.data ?? []).map(
        (prod: { code: string; name: string }) => ({
          ...prod,
          value: prod.code,
          label: prod.name,
          icon: PRODUCTS[0].icon,
          description: PRODUCTS[0].description,
          badge: (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <p>POPULAR CHOICE</p>
              <img
                src={MultiStarThemeIcon}
                style={{ width: "13px", height: "13px", fill: "#006763" }}
              />
            </Box>
          ),
        }),
      );
      if (mapped.length > 0) setProductOptions(mapped);
    } catch (error) {
      console.error("Failed to fetch products", error);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProducts();
  }, [fetchProducts]);

  const resolvedProducts: ProductOption[] =
    productOptions.length > 0 ? productOptions : PRODUCTS;

  const startCooldown = useCallback((channel: "mobile" | "email"): void => {
    const setter =
      channel === "mobile" ? setMobileResendCooldown : setEmailResendCooldown;
    setter(RESEND_COOLDOWN_SECONDS);
    const interval = setInterval(() => {
      setter((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);
  const handleInvokCreateApi = async (
    values: Step1FormValues,
  ): Promise<void> => {
    const fullValues: Step1Values = {
      ...values,
      isMobileVerified: mobileVerified,
      isEmailVerified: emailVerified,
    };

    const data = fields.map((field) => {
      const key = field.name as keyof Step1Values;
      const raw = fullValues[key];
      let formFieldValue: string;
      if (raw === null || raw === undefined) formFieldValue = "";
      else if (typeof raw === "boolean")
        formFieldValue = raw ? "true" : "false";
      else {
        formFieldValue = String(raw);
        if (formFieldValue === "Co-Applicant") formFieldValue = "student";
      }
      return {
        formFieldId: field.id,
        formFieldKey: field.name,
        formFieldValue,
      };
    });

    setIsBtnLoading(true);
    const payload: any = {
      templateId: 1,
      formName: "Eligibility Check",
      mobileNumber: values.phone_number,
      data,
    };
    if (trackingId) payload.trackingId = trackingId;
    const urlParams = new URLSearchParams(window.location.search);
    const leadOriginSource = urlParams.get("leadoriginsource") || "";
    const subSource = urlParams.get("source") || "";
    const subSubSource = urlParams.get("subsubsource") || "";
    const utmSource = urlParams.get("utm_source") || "";
    const utmCampaign = urlParams.get("utm_campaign") || "";
    const utmPartnerLeadId = urlParams.get("utm_partner_lead_id") || "";
    const adgroup = urlParams.get("adgroup") || "";
    const keywords = urlParams.get("keywords") || "";
    const gclid = urlParams.get("gclid") || "";

    if (leadOriginSource) payload.leadOriginSource = leadOriginSource;
    if (subSource) payload.subSource = subSource;
    if (subSubSource) payload.subSubSource = subSubSource;
    if (utmSource) payload.utm_source = utmSource;
    if (utmCampaign) payload.utm_campaign = utmCampaign;
    if (utmPartnerLeadId) payload.utmPartnerLeadId = utmPartnerLeadId;
    if(adgroup) payload.adgroup = adgroup;
    if(keywords) payload.keywords = keywords;
    if(gclid) payload.gclid = gclid;

    try {
      const resp = isAuthenticated
        ? await postsServices.addPost(
            "api/v1/user-lead/create-verified",
            payload,
          )
        : await postsServices.addPost("api/v1/user-lead/create", payload);
      if (resp.status === 201) {
        dispatch(setTrackingId(resp.data.data.trackingId));
        dispatch(setCurrentTrackingId(resp.data.data.trackingId));
        dispatch(setSelectedProduct(values.product || "SLI"));
        dispatch(setBasicDetails(fullValues));
        dispatch(setIsMobileVerified(true));
        dispatch(setIsEmailVerified(true));
        onNext(fullValues);
      }
    } catch (error: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Something went wrong. Please try again.",
        }),
      );
    } finally {
      setIsBtnLoading(false);
    }
  };

  const validationSchema = useMemo(
    () => buildValidationSchema(fields),
    [fields],
  );
  const fieldGroups = useMemo(() => groupFields(fields), [fields]);

  const formik = useFormik<Step1FormValues>({
    initialValues: {
      product: initialValues?.product ?? "SLI",
      applicantType:
        (initialValues?.applicantType as ApplicantType) ??
        ("" as ApplicantType),
      firstName: initialValues?.firstName ?? "",
      lastName: initialValues?.lastName ?? "",
      phone_number: initialValues?.phone_number ?? "",
      email: initialValues?.email ?? "",
      pincode: initialValues?.pincode ?? "",
      terms_conditions: initialValues?.terms_conditions ?? false,
      privacy_policy: initialValues?.privacy_policy ?? false,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      // Extra guards beyond Yup: applicantType, OTP verification, checkboxes
      const hasPhoneField = fields.some(
        (f) => f.type === "phone" && !f.isRemoved,
      );
      const hasEmailField = fields.some(
        (f) => f.type === "email" && !f.isRemoved,
      );
      if (!values.applicantType) return;
      if (!values.terms_conditions) return;
      if (!values.privacy_policy) return;
      if (hasPhoneField && values.phone_number && !mobileVerified) return;
      if (hasEmailField && values.email && !emailVerified) return;
      void handleInvokCreateApi(values);
    },
  });

  // ── Scroll to first error (identical to Step2) ─────────────────────────────

  const scrollToFirstError = useCallback((): void => {
    if (!formRef.current) return;
    setTimeout(() => {
      const container = formRef.current;
      if (!container) return;
      const allFieldEls = Array.from(
        container.querySelectorAll("[data-field-name]"),
      ) as HTMLElement[];
      for (const fieldName of FIELD_PRIORITY_ORDER) {
        const el = allFieldEls.find(
          (e) => e.getAttribute("data-field-name") === fieldName,
        );
        if (!el) continue;
        const hasError =
          el.querySelector(".Mui-error") !== null ||
          el.querySelector(".MuiFormHelperText-root.Mui-error") !== null ||
          el.querySelector("[aria-invalid='true']") !== null;
        if (hasError) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          (
            el.querySelector("input, [tabindex]") as HTMLElement | null
          )?.focus();
          return;
        }
      }
    }, 100);
  }, []);

  // ── Continue click (identical pattern to Step2) ────────────────────────────

  const handleContinueClick = useCallback((): void => {
    setContinueClicked(true);

    const touchedAll = Object.keys(formik.values).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {} as Record<string, boolean>,
    );

    formik.setTouched(touchedAll, true).then(() => {
      scrollToFirstError();
    });

    void formik.submitForm();
  }, [formik, scrollToFirstError]);

  const handleSendMobileOtp = async (): Promise<void> => {
    await formik.setFieldTouched("phone_number", true, true);
    const error = await formik.validateField("phone_number");
    if (error !== undefined) return;
    try {
      const name =
        `${formik.values.firstName.trim()} ${formik.values.lastName.trim()}`.trim();
      const resp = await eligibilityCheckServices.sendOtp({
        name,
        mobileNumber: formik.values.phone_number.trim(),
      });
      if (resp?.data?.status === "success") {
        setMobileOtpMeta({
          key: resp.data?.data?.value ?? "",
          createdAt: resp.data?.data?.otpTime ?? new Date().toISOString(),
          mobileNumber: formik.values.phone_number.trim(),
        });
      }
    } catch (err: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to send OTP. Please try again.",
        }),
      );
      return;
    }
    setMobileOtp("");
    setOtpModal("mobile");
    startCooldown("mobile");
  };

  const handleSendEmailOtp = async (): Promise<void> => {
    await formik.setFieldTouched("email", true, true);
    const error = await formik.validateField("email");
    if (error !== undefined) return;
    try {
      const name =
        `${formik.values.firstName.trim()} ${formik.values.lastName.trim()}`.trim();
      const resp = await eligibilityCheckServices.sendOtp({
        name,
        email: formik.values.email.trim(),
      });
      if (resp?.data?.status === "success") {
        setEmailOtpMeta({
          key: resp.data?.data?.value ?? "",
          createdAt: resp.data?.data?.otpTime ?? new Date().toISOString(),
          email: formik.values.email.trim(),
        });
      }
    } catch (err: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to send OTP. Please try again.",
        }),
      );
      return;
    }
    setEmailOtp("");
    setOtpModal("email");
    startCooldown("email");
  };

  const handleVerifyMobile = async (): Promise<void> => {
    setVerifyingMobile(true);
    try {
      const verifyResp = await eligibilityCheckServices.verifyOtp({
        type: "mobile",
        mobileNumber:
          mobileOtpMeta?.mobileNumber ?? formik.values.phone_number.trim(),
        key: mobileOtpMeta?.key ?? "",
        mobileOtp,
        mobileOtpCrtdAt: mobileOtpMeta?.createdAt ?? "",
        userType: formik.values.applicantType ?? "Applicant",
        ...(trackingId ? { trackingId } : {}),
      });
      if (verifyResp?.data?.status === "success") {
        setMobileVerified(true);
        setOtpModal(null);
      } else
        dispatch(
          setStatusPopup({
            status: "error",
            message:
              verifyResp?.data?.message ||
              "OTP verification failed. Please try again.",
          }),
        );
    } catch (err: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "OTP verification failed. Please try again.",
        }),
      );
    } finally {
      setVerifyingMobile(false);
    }
  };

  const handleVerifyEmail = async (): Promise<void> => {
    setVerifyingEmail(true);
    try {
      const verifyResp = await eligibilityCheckServices.verifyOtp({
        type: "email",
        email: emailOtpMeta?.email ?? formik.values.email.trim(),
        key: emailOtpMeta?.key ?? "",
        emailOtp: emailOtp,
        emailOtpCrtdAt: emailOtpMeta?.createdAt ?? "",
        userType: formik.values.applicantType ?? "Student",
        ...(trackingId ? { trackingId } : {}),
      });
      if (verifyResp?.data?.status === "success") {
        setEmailVerified(true);
        setOtpModal(null);
      } else
        dispatch(
          setStatusPopup({
            status: "error",
            message:
              verifyResp?.data?.message ||
              "OTP verification failed. Please try again.",
          }),
        );
    } catch (err: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "OTP verification failed. Please try again.",
        }),
      );
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleResendMobile = async (): Promise<void> => {
    try {
      const name =
        `${formik.values.firstName.trim()} ${formik.values.lastName.trim()}`.trim();
      const resp = await eligibilityCheckServices.sendOtp({
        name,
        mobileNumber: formik.values.phone_number.trim(),
      });
      if (resp?.data.status === "success") {
        setMobileOtpMeta({
          key: resp.data?.data?.value ?? "",
          createdAt: resp.data?.data?.otpTime ?? new Date().toISOString(),
          mobileNumber: formik.values.phone_number.trim(),
        });
      }
    } catch (err: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to resend OTP. Please try again.",
        }),
      );
      return;
    }
    setMobileOtp("");
    startCooldown("mobile");
  };

  const handleResendEmail = async (): Promise<void> => {
    try {
      const name =
        `${formik.values.firstName.trim()} ${formik.values.lastName.trim()}`.trim();
      const resp = await eligibilityCheckServices.sendOtp({
        name,
        email: formik.values.email.trim(),
      });
      if (resp?.data?.status === "success") {
        setEmailOtpMeta({
          key: resp.data?.data?.value ?? "",
          createdAt: resp.data?.data?.otpTime ?? new Date().toISOString(),
          email: formik.values.email.trim(),
        });
      }
    } catch (err: any) {
      dispatch(
        setStatusPopup({
          status: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to resend OTP. Please try again.",
        }),
      );
      return;
    }
    setEmailOtp("");
    startCooldown("email");
  };
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const numericValue: string = e.target.value.replace(/\D/g, "");
    e.target.value = numericValue?.slice(0, 10); // Limit to 10 digits
    formik.handleChange(e);
    setMobileVerified(false);
    setMobileOtp("");
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    formik.handleChange(e);
    setEmailVerified(false);
    setEmailOtp("");
  };

   const handlePincodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const numericValue: string = e.target.value.replace(/\D/g, "");
    e.target.value = numericValue?.slice(0, 6); // Limit to 6 digits
    formik.handleChange(e);
  };

  const getChangeHandler = (field: Field) => {
    if (field.type === "phone") return handlePhoneChange;
    if (field.type === "email") return handleEmailChange;
    if (field.name === "pincode") return handlePincodeChange;
    return formik.handleChange;
  };

  const getOtpProps = (field: Field) =>
    field.type === "phone"
      ? {
          otpSent: mobileVerified,
          verified: mobileVerified,
          otpValue: mobileOtp,
          verifying: verifyingMobile,
          onSendOtp: handleSendMobileOtp,
          onVerify: handleVerifyMobile,
          onOtpChange: setMobileOtp,
        }
      : {
          otpSent: emailVerified,
          verified: emailVerified,
          otpValue: emailOtp,
          verifying: verifyingEmail,
          onSendOtp: handleSendEmailOtp,
          onVerify: handleVerifyEmail,
          onOtpChange: setEmailOtp,
        };

  const maskedPhone = (phone: string) => {
    if (phone.length < 4) return phone;
    return phone.slice(0, 2) + "*".repeat(phone.length - 4) + phone.slice(-2);
  };

  const maskedEmail = (email: string) => {
    const [local, domain] = email.split("@");
    if (!local || !domain) return email;
    return `${local.slice(0, 2)}${"*".repeat(Math.max(0, local.length - 2))}@${domain}`;
  };

  const hasPhoneField = fields.some((f) => f.type === "phone" && !f.isRemoved);
  const hasEmailField = fields.some((f) => f.type === "email" && !f.isRemoved);

  const mobilePendingError =
    continueClicked &&
    hasPhoneField &&
    !!formik.values.phone_number &&
    !mobileVerified
      ? "Mobile number verification is pending. Please enter the OTP to verify."
      : undefined;

  const emailPendingError =
    continueClicked && hasEmailField && !!formik.values.email && !emailVerified
      ? "Email verification is pending. Please enter the OTP to verify."
      : undefined;

  const applicantTypeMissing = continueClicked && !formik.values.applicantType;

  const renderDropdownField = (field: Field): React.ReactNode => (
    <Box key={field.id} sx={{ mb: 3 }}>
      <Typography sx={{ fontWeight: 700, mb: 2, fontSize: "1.2rem" }}>
        Select Product
      </Typography>
      {formik.touched.product && Boolean(formik.errors.product) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {formik.errors.product}
        </Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {productsLoading
          ? [0, 1].map((n) => (
              <Grid item xs={12} sm={6} key={n}>
                <ProductCardSkeleton />
              </Grid>
            ))
          : resolvedProducts.map((product) => (
              <Grid item xs={12} sm={6} key={product.value}>
                <Paper
                  onClick={() => formik.setFieldValue("product", product.value)}
                  elevation={0}
                  sx={{
                    p: 3.1,
                    borderRadius: 2.5,
                    minHeight: "200px",
                    border: "2px solid",
                    cursor: "pointer",
                    borderColor:
                      formik.values.product === product.value
                        ? "primary.main"
                        : "#E7E8EA",
                    bgcolor:
                      formik.values.product === product.value
                        ? "#EBF5F6"
                        : "#f4f5f8",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "primary.light",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(27,107,90,0.12)",
                    },
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>
                    <img
                      src={product.icon}
                      alt={product.label}
                      style={{ width: 48, height: 48 }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      mb: 0.75,
                      color: "text.primary",
                    }}
                  >
                    {product.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "text.secondary",
                      lineHeight: 1.5,
                      mb: 1.5,
                    }}
                  >
                    {product.description}
                  </Typography>
                  {product.badge !== null &&
                    product.badge !== undefined &&
                    product.badge !== "" && (
                      <Chip
                        label={product.badge}
                        size="small"
                        sx={{
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          bgcolor: "rgba(27,107,90,0.1)",
                          color: "primary.main",
                          height: 22,
                        }}
                      />
                    )}
                </Paper>
              </Grid>
            ))}
      </Grid>
    </Box>
  );

  const renderRadioField = (field: Field): React.ReactNode => {
    const options = field.options?.options ?? ["Student", "Co-Applicant"];
    return (
      <Box key={field.id} data-field-name="applicantType" sx={{ mb: 6 }}>
        <Typography sx={{ fontWeight: 700, mb: 1.5, fontSize: "20px" }}>
          {field.label}
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          {options.map((opt) => (
            <Box
              key={opt}
              role="radio"
              aria-checked={formik.values.applicantType === opt}
              tabIndex={0}
              onClick={() =>
                formik.setFieldValue("applicantType", opt as ApplicantType)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  formik.setFieldValue("applicantType", opt as ApplicantType);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: {
                  xs: 2,
                  md: 2.5,
                },
                py: 1.25,
                borderRadius: 2,
                border: "2px solid",
                borderColor:
                  formik.values.applicantType === opt
                    ? "primary.main"
                    : applicantTypeMissing
                      ? "error.main"
                      : "grey.300",
                bgcolor:
                  formik.values.applicantType === opt
                    ? "#0067630D"
                    : "background.paper",
                color:
                  formik.values.applicantType === opt
                    ? "#006763"
                    : "text.secondary",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "16px",
                fontFamily: "Jost",
                transition: "all 0.2s",
                userSelect: "none",
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              {opt === "Student" ? (
                <PersonOutlineIcon sx={{ fontSize: 18 }} />
              ) : (
                <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
              )}
              {opt}
            </Box>
          ))}
        </Box>
        {applicantTypeMissing && (
          <FormHelperText error sx={{ mt: 0.75, ml: 0.5 }}>
            Please select whether you are applying as a Student or a
            Co-applicant.
          </FormHelperText>
        )}
      </Box>
    );
  };

  const renderCheckboxField = (field: Field): React.ReactNode => {
    const name = field.name as "terms_conditions" | "privacy_policy";
    return (
      <Box key={field.id} data-field-name={name}>
        <FormControlLabel
          control={
            <Checkbox
              name={field.name}
              checked={Boolean(formik.values[name])}
              onChange={formik.handleChange}
              size="small"
              sx={{ "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-root": { padding: "2px 9px"} }}
            />
          }
          label={<HtmlLabel html={field.label} />}
          sx={{ alignItems: "flex-start", mb: 0.5 }}
        />
        {formik.touched[name] && formik.errors[name] && (
          <FormHelperText error sx={{ ml: 4 }}>
            {formik.errors[name] as string}
          </FormHelperText>
        )}
      </Box>
    );
  };

  const renderTextCell = (field: Field): React.ReactNode => {
    const otpPendingError =
      field.type === "phone"
        ? mobilePendingError
        : field.type === "email"
          ? emailPendingError
          : undefined;
    return (
      <TextFieldRenderer
        key={field.id}
        field={field}
        formik={formik}
        fieldSize={fieldSize}
        onChange={getChangeHandler(field)}
        otpPendingError={otpPendingError}
        {...getOtpProps(field)}
      />
    );
  };

  const renderGroup = (group: FieldGroup, idx: number): React.ReactNode => {
    if (group.kind === "singleton") {
      const { field } = group;
      if (field.type === "dropdown") return renderDropdownField(field);
      if (field.type === "radio") return renderRadioField(field);
      if (field.type === "checkbox") return null;
      return (
        <Grid
          container
          spacing={2}
          sx={{
            mb: 4,
          }}
          key={`group-${idx}`}
        >
          <Grid item xs={12} sm={6}>
            {renderTextCell(field)}
          </Grid>
        </Grid>
      );
    }
    return (
      <Grid container spacing={2} sx={{ mb: { xs: 2, sm: 4 }}} key={`group-${idx}`}>
        <Grid item xs={12} sm={6}>
          {renderTextCell(group.left)}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderTextCell(group.right)}
        </Grid>
      </Grid>
    );
  };

  const checkboxFields = fields
    .filter((f) => !f.isRemoved && f.type === "checkbox")
    .sort((a, b) => a.fieldOrder - b.fieldOrder);

  if (fields?.length === 0) return <Step1BasicInfoSkeleton />;

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Box sx={{ display: isMobile ? "column" : "flex", minWidth: 0, gap: 3 }}>
        <Box
          component="form"
          ref={formRef}
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <Box sx={{ mb: 4, bgcolor: isMobile ? "#EBF5F6" : "", p: { xs: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <img
                src={MultiStarIcon}
                style={{ width: "13px", height: "13px" }}
              />
              <Typography
                variant="body2"
                sx={{ color: "#251A00", fontWeight: 600 }}
              >
                Basic Info
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "1.6rem", md: "42px" },
                fontWeight: 700,
                lineHeight: 1.3,
                mb: 1,
              }}
            >
              What can we{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                help you with today?
              </Box>
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Provide your primary contact details to finalize your application,
              review your loan eligibility and lock in your preferential rates.
            </Typography>
          </Box>

          {/* ── Mobile Sidebar ── */}
          {isMobile && (
            <Sidebar
              currentStep={currentStep === 4 ? 3 : currentStep}
              completedSteps={completedSteps}
            />
          )}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              borderRadius: 3,
              border: isMobile ? "none" : "1px solid",
              borderColor: isMobile ? "transparent" : "grey.200",
              bgcolor: "#fffff",
            }}
          >
            {fieldGroups.map((group, idx) => renderGroup(group, idx))}

            {checkboxFields.length > 0 && (
              <Box sx={{ mb: 3 }}>
                {checkboxFields.map((field) => renderCheckboxField(field))}
              </Box>
            )}

            {!isMobile && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  disabled={isBtnLoading}
                  onClick={handleContinueClick}
                  sx={{
                    py: { xs: 1, md: 1.75 },
                    fontSize: { xs: "0.85rem", md: "1rem" },
                    fontWeight: 700,
                  }}
                >
                  {isBtnLoading ? "Loading..." : "Continue"}
                </Button>
              </Box>
            )}
          </Paper>
        </Box>

        {currentStep <= 3 && (
          <Box
            sx={{
              width: isMobile ? "100%" : 260,
              flexShrink: 0,
              bgcolor: "#EBF5F6",
              px: 2,
              py: 3,
              position: "sticky",
              top: 64,
            }}
          >
            <RightPanel isShowRating />
          </Box>
        )}
        {isMobile && (
          <StickyFooter progress={progress} onContinue={handleContinueClick} />
        )}
      </Box>

      <OtpModal
        open={otpModal === "mobile"}
        type="mobile"
        maskedDestination={maskedPhone(formik.values.phone_number)}
        otpValue={mobileOtp}
        verifying={verifyingMobile}
        resendCooldown={mobileResendCooldown}
        onOtpChange={setMobileOtp}
        onVerify={handleVerifyMobile}
        onResend={handleResendMobile}
        onClose={() => setOtpModal(null)}
      />
      <OtpModal
        open={otpModal === "email"}
        type="email"
        maskedDestination={maskedEmail(formik.values.email)}
        otpValue={emailOtp}
        verifying={verifyingEmail}
        resendCooldown={emailResendCooldown}
        onOtpChange={setEmailOtp}
        onVerify={handleVerifyEmail}
        onResend={handleResendEmail}
        onClose={() => setOtpModal(null)}
      />
    </>
  );
};

export default memo(Step1BasicInfo);
